import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { PageHeader } from '../components/PageHeader';
import { errorText, useApi } from '../lib/api';
import { healthSources, isUntrusted, platformLabel } from '../lib/health/healthApi';

const th = 'px-2 py-1 text-left';
const td = 'px-2 py-1';

/**
 * Health sources (health intake, `GET /admin/health/sources`): every app that wrote workouts, per platform, with how many players
 * and workouts and its configured trusted-source tier. A source the server couldn't tier is "not in trustedSources": promote it
 * by adding a row to Health rules' trusted sources (the editor is linked) and publishing.
 */
export function HealthSourcesPage() {
  const { client } = useApi();
  const q = useQuery({ queryKey: ['admin-health-sources'], queryFn: () => healthSources(client) });
  const rows = [...(q.data?.sources ?? [])].sort(
    (a, b) => b.workouts - a.workouts || a.sourceApp.localeCompare(b.sourceApp),
  );
  const unknown = rows.filter(isUntrusted).length;

  return (
    <>
      <PageHeader
        title="Health sources"
        description="Every app that wrote workouts to Apple Health or Health Connect for our players, with its trusted-source tier. Promote an unknown app in Health rules."
      >
        <Link to="/content/health-rules" className="text-sm underline">
          Edit trusted sources
        </Link>
      </PageHeader>
      {q.isPending && <p className="text-sm">Loading…</p>}
      {q.error && (
        <p className="text-sm text-blam">
          {errorText(q.error).includes('404')
            ? 'Not available yet (an older API).'
            : errorText(q.error)}
        </p>
      )}
      {q.data && rows.length === 0 && <p className="text-sm">No workouts yet.</p>}
      {rows.length > 0 && (
        <section className="overflow-x-auto rounded border-2 border-ink bg-white p-4">
          <p className="mb-2 text-xs text-bruise">
            {rows.length} sources, {unknown} not in trustedSources.
          </p>
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className={th}>Source app</th>
                <th className={th}>Platform</th>
                <th className={th}>Players</th>
                <th className={th}>Workouts</th>
                <th className={th}>Tier</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={`${s.platform}|${s.sourceApp}`} className="border-t border-ink/20">
                  <td className={`${td} font-mono`}>{s.sourceApp}</td>
                  <td className={td}>{platformLabel(s.platform)}</td>
                  <td className={td}>{s.players}</td>
                  <td className={td}>{s.workouts}</td>
                  <td className={td}>
                    {isUntrusted(s) ? (
                      <span className="rounded bg-caption px-1.5 text-xs font-semibold">
                        not in trustedSources
                      </span>
                    ) : (
                      s.tier
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}
